import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Box,
  Snackbar,
  Alert,
  Typography,
  IconButton,
  InputAdornment,
  FormControl,
  FormHelperText,
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ClearIcon from '@mui/icons-material/Clear';
import WebContainer from "../WebContainer/Index";
import Title from "../Title/Index";
import "./index.css";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../Utils/useApi";
import axios from "axios";
import { setFormFilled } from "../../Utils/formStatus";

const Enquiry = () => {
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required("Name is required")
      .min(3, "Name must be at least 3 characters")
      .max(20, "Name must not exceed 20 characters")
      .matches(/^[A-Z a-z]+$/, "Name must contain only alphabets"),
    email: Yup.string()
      .email("Invalid email format"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .test("is-valid-phone", "Invalid phone number format", (value) => {
        const phoneNumberPattern = /(?:\+?\d{1,3})?[-.\s]?(\d{10})/;
        return phoneNumberPattern.test(value);
      }),
    description: Yup.string()
      .max(500, "description must not exceed 500 characters"),
    resume: Yup.mixed()
      .required("Resume is required")
      .test("fileSize", "File too large (max 5MB)", value => !value || value.size <= 5 * 1024 * 1024)
      .test("fileType", "Unsupported file format (PDF, DOC, DOCX only)", value =>
        !value || ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(value.type)
      ),
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ open: false, message: "", severity: "success" });
  };

  const extractPhoneNumber = (input) => {
    const phoneNumberPattern = /(?:\+?\d{1,3})?[-.\s]?(\d{10})/;
    const match = input.match(phoneNumberPattern);
    return match ? match[1] : "";
  };

  return (
    <WebContainer _parentClass={"BusinessEnquiry"} sectionID={"BusinessEnquiry"}>
      <Title 
        secondHeading={"Work with us"} 
        firstHeading={'Reach Out'} 
        _class={"mb-3"} 
      />
      
      <Formik
        initialValues={{
          fullName: "",
          email: "",
          mobile: "",
          description: "",
          resume: null,
        }}
        validationSchema={validationSchema}
        validateOnBlur
        validateOnChange
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          const extractedPhone = extractPhoneNumber(values.mobile);
          const formData = new FormData();
          formData.append("fullName", values.fullName);
          formData.append("email", values.email);
          formData.append("mobile", extractedPhone);
          formData.append("description", values.description);
          formData.append("resume", values.resume); 
          
          // console.log("Form Data", formData);

          axios.post(`${API_BASE_URL}career_submit.php`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
            .then((response) => {
              // console.log("Success:", response);
              if (response.data.status === "success") {
                // console.log("Success:", response.data);
                setFormFilled("true", response.data);
                showNotification("Form submitted successfully!", "success");
                resetForm();
                navigate("/thank-you");
              } else {
                showNotification(
                  "Form submission failed. Please try again.",
                  "error"
                );
              }
            })
            .catch((error) => {
              console.error("Error:", error);
              if (error.response) {
                const status = error.response.status;
                const errorMessage =
                  error.response.data.message || "An error occurred";

                if (status === 400) {
                  showNotification(
                    "Bad request. Please check your input.",
                    "error"
                  );
                } else if (status === 401) {
                  showNotification(
                    "Unauthorized access. Please log in.",
                    "error"
                  );
                } else if (status === 500) {
                  showNotification(
                    "Server error. Please try again later.",
                    "error"
                  );
                } else {
                  showNotification(`Error: ${errorMessage}`, "error");
                }
              } else if (error.request) {
                showNotification(
                  "No response from the server. Please try again.",
                  "error"
                );
              } else {
                showNotification("Error: " + error.message, "error");
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({ errors, touched, handleSubmit, isSubmitting, validateForm, values, setFieldValue }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validateForm().then((formErrors) => {
                const errorMessages = Object.values(formErrors);
                if (errorMessages.length > 0) {
                  showNotification(
                    "Please fix all errors before submitting.",
                    "error"
                  );
                } else {
                  handleSubmit();
                }
              });
            }}
          >
            <Box
              className="rowFormControl d-flex gap-3"
              data-aos="fade-up"
              data-aos-offset="200"
              data-aos-duration="500"
              data-aos-once="true"
              data-aos-easing="ease-in-sine"
            >
              <Field name="fullName">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="FULL NAME*"
                    margin="normal"
                    variant="outlined"
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
              <Field name="mobile">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="MOBILE NO*"
                    margin="normal"
                    variant="outlined"
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
              <Field name="email">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="EMAIL ID"
                    margin="normal"
                    variant="outlined"
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Box>

           <Box
  className="rowFormControl"
  data-aos="fade-up"
  data-aos-offset="200"
  data-aos-duration="500"
  data-aos-once="true"
  data-aos-easing="ease-in-sine"
  sx={{ mt: 2 }}
>
  <FormControl fullWidth>
    <TextField
      label="RESUME*"
      variant="outlined"
      margin="normal"
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {values.resume ? <DescriptionIcon /> : <CloudUploadIcon sx={{ opacity: 0.5 }} />}
          </InputAdornment>
        ),
        endAdornment: values.resume && (
          <InputAdornment position="end">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setFieldValue("resume", null);
                document.getElementById('resume-upload').value = '';
              }}
              edge="end"
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        readOnly: true,
      }}
      onClick={() => document.getElementById('resume-upload').click()}
      value={values.resume ? values.resume.name : ''}
      placeholder={!values.resume ? "Upload your file" : ""}
      error={touched.resume && Boolean(errors.resume)}
      helperText={
        touched.resume && errors.resume ? (
          errors.resume
        ) : (
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            Supported formats: .pdf, .doc, .docx (Max 5MB)
          </Typography>
        )
      }
      sx={{
        cursor: 'pointer',
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: touched.resume && errors.resume ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: touched.resume && errors.resume ? '#f44336' : 'rgba(0, 0, 0, 0.87)',
          },
        },
        '& .MuiInputBase-input': {
          cursor: 'pointer',
          color: !values.resume ? 'rgba(0, 0, 0, 0.6)' : 'inherit',
        },
      }}
    />
    <input
      id="resume-upload"
      name="resume"
      type="file"
      accept=".pdf,.doc,.docx"
      style={{ display: 'none' }}
      onChange={(event) => {
        const file = event.currentTarget.files[0];
        if (file) {
          setFieldValue("resume", file);
        }
      }}
    />
    {values.resume && (
      <FormHelperText sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
        <Typography variant="body2" component="span">
          {(values.resume.size / 1024 / 1024).toFixed(2)} MB
        </Typography>
      </FormHelperText>
    )}
  </FormControl>
</Box>

            <Box
              className="rowFormControl"
              data-aos="fade-up"
              data-aos-offset="200"
              data-aos-duration="500"
              data-aos-once="true"
              data-aos-easing="ease-in-sine"
            >
              <Field name="description">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Cover Letter / Description"
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={4}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Box>

            <Box
              textAlign="center"
              data-aos="fade-up"
              data-aos-offset="200"
              data-aos-duration="500"
              data-aos-once="true"
              data-aos-easing="ease-in-sine"
              sx={{ mt: 3 }}
            >
              <button
                type="submit"
                className="customButton"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting' : 'Submit'}
                {isSubmitting && <span className="dot-flashing"></span>}
              </button>
            </Box>
          </Form>
        )}
      </Formik>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </WebContainer>
  );
};

export default Enquiry;